import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SessionAnalyticsService {
  private currentStep: string | null = null;
  private stepsStatus: Record<string, { name: string | null; event_timestamp: string | null }> = {
    step_1: { name: null, event_timestamp: null },
    step_2: { name: null, event_timestamp: null },
    step_3: { name: null, event_timestamp: null },
    step_4: { name: null, event_timestamp: null },
    step_5: { name: null, event_timestamp: null },
    step_6: { name: null, event_timestamp: null },
  };

  private retryCount = 0;
  // private maxRetries = 5;
  private retryDelay = 1000;
  private pendingEvents: Array<{
    eventName: string;
    eventCategory: string;
    eventLabel: string;
    value: number;
    additionalParams: Record<string, any>;
    priority: number;
  }> = [];

  private isInitialized = false;
  private readonly STEP_NAMES: Record<string, string> = {
    step_1: 'Session started',
    step_2: 'Sensitive data entered',
    step_3: 'External link started',
    step_4: 'External link ended',
    step_5: 'Response received & displayed',
    step_6: 'Session ended',
  };
  private platformId = inject(PLATFORM_ID);

  constructor(private router: Router) {
    this.initializeGtag();
  }

  private initializeGtag(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isGtagInitialized()) {
        console.log('Gtag already initialized');
        this.isInitialized = true;
        this.processPendingEvents();
        return;
      }

      const checkInterval = setInterval(() => {
        console.log('Checking if gtag is initialized...');
        if (this.isGtagInitialized()) {
          this.isInitialized = true;
          this.processPendingEvents();
          clearInterval(checkInterval);
          return;
        }

        console.log('Gtag not initialized yet. Retrying...');

        this.retryCount++;
        // if (this.retryCount >= this.maxRetries) {
        //   clearInterval(checkInterval);
        //   console.error('Max retries reached for gtag initialization. Some events may not be tracked.');
        //   this.processPendingEvents();
        // }
      }, this.retryDelay);
    }
  }

  private processPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;

    this.pendingEvents.sort((a, b) => b.priority - a.priority);

    console.log(`Processing ${this.pendingEvents.length} pending events...`);

    const eventsToProcess = [...this.pendingEvents];
    this.pendingEvents = [];

    for (const event of eventsToProcess) {
      this.sendEventToGtag(
        event.eventName,
        event.eventCategory,
        event.eventLabel,
        event.value,
        event.additionalParams
      );
    }
  }

  private isGtagInitialized(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  private sendEventToGtag(
    eventName: string,
    eventCategory: string,
    eventLabel: string,
    value: number,
    additionalParams: Record<string, any> = {}
  ): void {
    try {
      if (!this.isGtagInitialized()) {
        console.warn(`Cannot send event ${eventName} - gtag not initialized`);
        return;
      }

      const eventPayload = {
        event_category: eventCategory,
        event_label: eventLabel,
        value: value,
        ...additionalParams,
      };

      window.gtag('event', eventName, eventPayload);
      console.log(`Event sent to GA: ${eventName} - ${eventLabel}`);
      console.log(`Full Event Payload:`, eventPayload);
    } catch (error) {
      console.error('Error sending event to Google Analytics:', error);
    }
  }

  public trackEvent(
    eventName: string,
    eventCategory: string,
    eventLabel: string,
    value: number,
    additionalParams: Record<string, any> = {},
    priority: number = 1
  ): void {
    try {
      const globalData: any = this.router.getCurrentNavigation()?.extras.info || {};
      const orderId = globalData?.order_id || 'Unknown1';

      const fullParams = {
        order_id: orderId,
        ga_order_id: orderId,
        ...additionalParams,
      };

      if (!this.isGtagInitialized()) {
        this.pendingEvents.push({
          eventName,
          eventCategory,
          eventLabel,
          value,
          additionalParams: fullParams,
          priority,
        });

        console.log(`Event queued (gtag not ready): ${eventName} - ${eventLabel}`);
        return;
      }

      this.sendEventToGtag(eventName, eventCategory, eventLabel, value, fullParams);
    } catch (error) {
      console.error('Error in trackEvent:', error);
      this.pendingEvents.push({
        eventName,
        eventCategory,
        eventLabel,
        value,
        additionalParams,
        priority,
      });
    }
  }

  public trackStep(
    stepNumber: number,
    stepName: string = '',
    additionalParams: Record<string, any> = {}
  ): void {
    if (stepNumber < 1 || stepNumber > 7) {
      console.error(`Invalid step number: ${stepNumber}. Must be between 1 and 7.`);
      return;
    }

    const stepKey = `step_${stepNumber}`;

    const finalStepName = stepName || this.STEP_NAMES[stepKey] || `Step ${stepNumber}`;

    if (!finalStepName) {
      console.error(`No step name available for step ${stepNumber}`);
      return;
    }

    const timestamp = new Date().toISOString();
    this.currentStep = stepKey;

    this.stepsStatus[stepKey] = {
      name: finalStepName,
      event_timestamp: timestamp,
    };

    const eventParams = {
      [`${stepKey}_name`]: finalStepName,
      [`${stepKey}_timestamp`]: timestamp,
      all_steps: JSON.stringify(this.stepsStatus),
      current_step: stepKey,
      ...additionalParams,
    };

    this.trackEvent(stepKey, 'user_journey', finalStepName, 1, eventParams, 10);

    this.trackEvent(
      'global_data',
      'user_journey',
      `Updated: ${finalStepName}`,
      1,
      {
        ...eventParams,
        all_steps: JSON.stringify(this.stepsStatus),
      },
      5
    );
  }

  public trackStepsFromSession(
    globalDataWithSteps: any,
    sessionData: any,
    specificStep: string
  ): void {
    if (!sessionData) {
      console.warn('Session data is undefined or null.');
      return;
    }

    if (!globalDataWithSteps) {
      globalDataWithSteps = {};
    }

    const stepName = this.STEP_NAMES[specificStep] || specificStep;

    if (!stepName) {
      console.error(`Unknown step: ${specificStep}`);
      return;
    }

    const timestamp = new Date().toISOString();

    this.stepsStatus[specificStep] = {
      name: stepName,
      event_timestamp: timestamp,
    };

    this.currentStep = specificStep;

    const orderId = sessionData.order_id || globalDataWithSteps?.order_id || 'Unknown';
    const gatewayRecommendation =
      sessionData.gatewayRecommendation || globalDataWithSteps?.gatewayRecommendation || 'Unknown';

    const stepEventParams = {
      [`${specificStep}_name`]: stepName,
      [`${specificStep}_timestamp`]: timestamp,
      order_id: orderId,
      ga_order_id: orderId,
      gatewayRecommendation: gatewayRecommendation,
      ga_gatewayRecommendation: gatewayRecommendation,
      all_steps: JSON.stringify(this.stepsStatus),
      current_step: specificStep,
    };

    this.trackEvent(specificStep, 'user_journey', stepName, 1, stepEventParams, 10);

    this.trackEvent(
      'global_data',
      'user_journey',
      `Updated: ${stepName}`,
      1,
      {
        ...stepEventParams,
        ...globalDataWithSteps,
        order_id: orderId,
        ga_order_id: orderId,
        gatewayRecommendation: gatewayRecommendation,
        ga_gatewayRecommendation: gatewayRecommendation,
        current_step: specificStep,
        all_steps: JSON.stringify(this.stepsStatus),
      },
      5
    );
  }

  public trackAllSteps(sessionData: any = {}, additionalParams: Record<string, any> = {}): void {
    for (let i = 1; i <= 7; i++) {
      const stepKey = `step_${i}`;

      if (this.stepsStatus[stepKey].event_timestamp) {
        continue;
      }

      const stepName = this.STEP_NAMES[stepKey];

      const timestamp = new Date().toISOString();

      this.stepsStatus[stepKey] = {
        name: stepName,
        event_timestamp: timestamp,
      };

      const eventParams = {
        [`${stepKey}_name`]: stepName,
        [`${stepKey}_timestamp`]: timestamp,
        all_steps: JSON.stringify(this.stepsStatus),
        current_step: stepKey,
        ...additionalParams,
      };

      this.trackEvent(stepKey, 'user_journey', stepName, 1, eventParams, 10);
    }

    this.trackEvent(
      'global_data',
      'user_journey',
      'All Steps Tracked',
      1,
      {
        all_steps: JSON.stringify(this.stepsStatus),
        ...additionalParams,
      },
      5
    );
  }

  public ensureStepSequence(currentStepNumber: number): void {
    for (let i = 1; i < currentStepNumber; i++) {
      const stepKey = `step_${i}`;

      if (!this.stepsStatus[stepKey].event_timestamp) {
        this.trackStep(i);
      }
    }
  }

  public trackEventsFromGlobaleData(): any {
    if (isPlatformBrowser(this.platformId)) {
      const globaleData: any = this.router.getCurrentNavigation()?.extras.info || {
        order_id: 'Unknown',
      };

      return {
        ga_account: 'EquitiPay',
        ga_session_ID: globaleData?.id || 'Unknown',
        ga_order_id: globaleData?.order_id || 'Unknown',
        order_id: globaleData?.order_id || 'Unknown',
        ga_retailer_name: globaleData?.retailer_name || 'Unknown',
        ga_amount: globaleData?.amount || 'Unknown',
        ga_currency: globaleData?.currency || 'Unknown',
        ga_customer_country_name: globaleData?.customer_country_name || 'Unknown',
        ga_customer_account_id: globaleData?.customer_account_id || 'Unknown',
        ga_customer_account_currency:
          globaleData?.customer_account_currency_alphabetic_code || 'Unknown',
        ga_customer_id: globaleData?.customer_id || 'Unknown',
        ga_customer_name: globaleData?.customer_name || 'Unknown',
        ga_selected_mop: globaleData?.selected_mop || 'Unknown',
        ga_mop: globaleData?.mop || 'Unknown',
        ga_service_mean: globaleData?.service_mean || 'Unknown',
        ga_service_name: globaleData?.service_name || 'Unknown',
        ga_gatewayRecommendation: globaleData?.gatewayRecommendation || 'Unknown',
        gatewayRecommendation: globaleData?.gatewayRecommendation || 'Unknown',
        current_step: this.currentStep,
        all_steps: JSON.stringify(this.stepsStatus),
        ga_value: 1,
        ga_page_title: document.title,
        ga_page_path: window.location.pathname,
        ga_page_location: window.location.href,
      };
    } else {
      return {};
    }
  }

  public flushEvents(): void {
    this.processPendingEvents();
  }
}
